/**
 * Initializes accessible toggles using CSS Grid for smooth height transitions.
 * Automatically handles ARIA attributes, unique IDs, and keyboard support.
 *
 * @param {string} sectionSelector - The root class of the toggle component.
 * @param {string} triggerSelector - The button class that triggers the toggle.
 * @param {string} contentSelector - The class of the hidden content area.
 * @returns {void}
 */
export const initSectionToggles = (sectionSelector, triggerSelector, contentSelector) => {
  const sections = document.querySelectorAll(sectionSelector);

  sections.forEach((section, index) => {
    if (section.dataset.toggleInitialized === 'true') return;

    const btn = section.querySelector(triggerSelector);
    const content = section.querySelector(contentSelector);
    const textSpan = btn?.querySelector('.btn-text');

    if (!btn || !content) return;

    section.dataset.toggleInitialized = 'true';

    // 1. Setup unique IDs and ARIA
    const uniqueId = `grid-toggle-${index}`;
    content.id = uniqueId;
    btn.setAttribute('aria-controls', uniqueId);
    btn.setAttribute('aria-expanded', String(!content.hidden));
    content.setAttribute('tabindex', '-1');

    /**
     * Updates text labels and aria-expanded state.
     * @param {boolean} isOpen
     */

    const updateAttributes = (isOpen) => {
      const label = isOpen ? btn.dataset.textLess : btn.dataset.textMore;
      if (textSpan) textSpan.textContent = label;
      if (label) {
        btn.setAttribute('aria-label', label);
      }
      btn.setAttribute('aria-expanded', String(isOpen));
    };

    /**
     * Toggles a section open or closed.
     *
     * @returns {void}
     */
    const toggle = () => {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      const nextState = !isOpen;

      if (nextState) {
        // OPENING
        content.hidden = false;
        // Small delay to ensure the browser registers 'hidden = false' before animation
        requestAnimationFrame(() => {
          section.classList.add('is-open');
          updateAttributes(true);
        });
      } else {
        // CLOSING
        section.classList.remove('is-open');
        updateAttributes(false);

        // Wait for CSS transition to finish before hiding from screen readers
        content.addEventListener('transitionend', () => {
          if (!section.classList.contains('is-open')) {
            content.hidden = true;
          }
        }, { once: true });
      }
    };

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      toggle();
    });

    // Escape key support
    section.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') {
        if (section.contains(document.activeElement)) {
          toggle();
          btn.focus();
        }
      }
    });
  });
};
